import { useState, useRef, useEffect } from 'react';
import Portal from './Portal';

export default function Tooltip({
  content,
  children,
  position = 'top', // 'top' | 'bottom' | 'auto'
  delay = 120,
  disabled = false,
  className = '',
  as = 'div',
}) {
  const [visible, setVisible] = useState(false);
  const [placement, setPlacement] = useState(position === 'auto' ? 'top' : position);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timerRef = useRef(null);

  const show = () => {
    if (disabled || !content) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;
        setCoords({
          top: rect.top + scrollY - 6,
          left: rect.left + scrollX + rect.width / 2,
        });
        setVisible(true);
      }
    }, delay);
  };

  const hide = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
  };

  useEffect(() => {
    if (visible && tooltipRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      const fitsTop = triggerRect.top >= tooltipRect.height + 10;
      const actualPlacement = (position === 'auto' || position === 'top')
        ? (fitsTop ? 'top' : 'bottom')
        : position;

      const top = actualPlacement === 'top'
        ? triggerRect.top + scrollY - 6
        : triggerRect.bottom + scrollY + 6;

      const halfWidth = tooltipRect.width / 2;
      const minLeft = scrollX + 8 + halfWidth;
      const maxLeft = scrollX + window.innerWidth - 8 - halfWidth;
      let left = triggerRect.left + scrollX + triggerRect.width / 2;
      left = Math.max(minLeft, Math.min(maxLeft, left));

      setPlacement(actualPlacement);
      setCoords({ top, left });
    }
  }, [visible, position]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!content) return children;

  const Component = as;

  return (
    <>
      <Component
        ref={triggerRef}
        className={`ui-tooltip-trigger ${className}`.trim()}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </Component>
      {visible && (
        <Portal>
          <div
            ref={tooltipRef}
            className={`ui-tooltip ui-tooltip--${placement}`}
            style={{
              top: coords.top,
              left: coords.left,
            }}
            role="tooltip"
          >
            {content}
          </div>
        </Portal>
      )}
    </>
  );
}

